import React, { useState, useEffect, useMemo } from 'react';
import api from '../api';

const EMPTY_FORM = { name: '', category: '', unit: '', quantity_on_hand: '', par_level: '', storage_location: '', is_food: true };

export default function InventoryGrid({ filterFood }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [belowPar, setBelowPar] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [editingQty, setEditingQty] = useState(null);
  const [editQtyVal, setEditQtyVal] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [historyItemId, setHistoryItemId] = useState(null);
  const [historyLog, setHistoryLog] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  useEffect(() => { fetchInventory(); }, [belowPar]);

  async function fetchInventory() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (belowPar) params.set('below_par', 'true');
      if (filterFood !== undefined) params.set('is_food', String(filterFood));
      const { data } = await api.get(`/inventory?${params}`);
      setItems(data);
    } catch (err) {
      setError('Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }

  const categories = useMemo(() =>
    [...new Set(items.map(i => i.category).filter(Boolean))].sort(), [items]);

  const locations = useMemo(() =>
    [...new Set(items.map(i => i.storage_location).filter(Boolean))].sort(), [items]);

  async function handleAdd(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const { data } = await api.post('/inventory', {
        ...form,
        quantity_on_hand: parseFloat(form.quantity_on_hand) || 0,
        par_level: parseFloat(form.par_level) || 0,
        is_food: form.is_food,
      });
      setItems(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      setForm(EMPTY_FORM);
      setShowForm(false);
      flash('✅ Item added');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add item');
    } finally {
      setSaving(false);
    }
  }

  async function saveQty(item) {
    const qty = parseFloat(editQtyVal);
    if (isNaN(qty)) { setEditingQty(null); return; }
    try {
      await api.put(`/inventory/${item.id}`, { quantity_on_hand: qty });
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, quantity_on_hand: qty } : i));
      flash('✅ Quantity updated');
    } catch {
      setError('Failed to update quantity');
    } finally {
      setEditingQty(null);
    }
  }

  function startEdit(item) {
    setEditingItem(item.id);
    setEditForm({
      name: item.name,
      category: item.category || '',
      unit: item.unit,
      quantity_on_hand: item.quantity_on_hand,
      par_level: item.par_level,
      storage_location: item.storage_location || '',
      is_food: item.is_food,
    });
  }

  async function saveEdit(itemId) {
    setEditSaving(true);
    try {
      const { data } = await api.put(`/inventory/${itemId}`, {
        ...editForm,
        quantity_on_hand: parseFloat(editForm.quantity_on_hand) || 0,
        par_level: parseFloat(editForm.par_level) || 0,
      });
      setItems(prev => prev.map(i => i.id === itemId ? data : i));
      setEditingItem(null);
      flash('✅ Item updated');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally {
      setEditSaving(false);
    }
  }

  async function toggleHistory(itemId) {
    if (historyItemId === itemId) { setHistoryItemId(null); return; }
    setHistoryItemId(itemId);
    setHistoryLoading(true);
    try {
      const { data } = await api.get(`/inventory/${itemId}/history`);
      setHistoryLog(data);
    } catch { setHistoryLog([]); }
    finally { setHistoryLoading(false); }
  }

  function flash(msg) {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  }

  const filtered = items.filter(i => {
    if (search && !i.name.toLowerCase().includes(search.toLowerCase()) &&
        !(i.category || '').toLowerCase().includes(search.toLowerCase())) return false;
    if (categoryFilter && i.category !== categoryFilter) return false;
    if (locationFilter && i.storage_location !== locationFilter) return false;
    return true;
  });

  const parPct = (item) => item.par_level > 0
    ? Math.min(100, Math.round((item.quantity_on_hand / item.par_level) * 100))
    : 100;
  const parClass = (pct) => pct < 25 ? 'low' : pct < 60 ? 'mid' : 'good';
  const parBadge = (pct) => pct < 25 ? 'badge-red' : pct < 60 ? 'badge-amber' : 'badge-green';

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        {/* Filters row */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
          <input
            type="text"
            placeholder="Search items..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: 160 }}
          />
          <select
            value={categoryFilter}
            onChange={e => setCategoryFilter(e.target.value)}
            style={{ minWidth: 120 }}
          >
            <option value="">All categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select
            value={locationFilter}
            onChange={e => setLocationFilter(e.target.value)}
            style={{ minWidth: 120 }}
          >
            <option value="">All locations</option>
            {locations.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={belowPar} onChange={e => setBelowPar(e.target.checked)} />
            Below par only
          </label>
        </div>
        {/* Actions — always right-aligned, never wrapped into filters */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
          <button className="btn btn-ghost btn-sm" onClick={fetchInventory}>↻ Refresh</button>
          <button className="btn btn-primary btn-sm" onClick={() => { setShowForm(!showForm); setError(''); }}>
            {showForm ? '✕ Cancel' : '+ Add Item'}
          </button>
        </div>
      </div>

      {/* Messages */}
      {successMsg && <div className="alert alert-success mb-4">{successMsg}</div>}
      {error && <div className="alert alert-error mb-4">{error}</div>}
      {belowPar && filtered.length > 0 && (
        <div className="alert alert-warning mb-4">⚠️ {filtered.length} items below par level</div>
      )}

      {/* Add Item Form */}
      {showForm && (
        <div className="card mb-4">
          <div className="card-header">New Inventory Item</div>
          <div className="card-body">
            <form onSubmit={handleAdd}>
              <div className="form-row">
                <div className="form-group">
                  <label>Item Name *</label>
                  <input
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. Ahi Tuna"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <input
                    list="add-item-cats"
                    value={form.category}
                    onChange={e => setForm({ ...form, category: e.target.value })}
                    placeholder="Pick or type a category…"
                  />
                  <datalist id="add-item-cats">
                    {categories.map(c => <option key={c} value={c} />)}
                  </datalist>
                </div>
                <div className="form-group">
                  <label>Unit *</label>
                  <input
                    value={form.unit}
                    onChange={e => setForm({ ...form, unit: e.target.value })}
                    placeholder="lb, ea, case, bottle..."
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Quantity on Hand</label>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    value={form.quantity_on_hand}
                    onChange={e => setForm({ ...form, quantity_on_hand: e.target.value })}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Par Level</label>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    value={form.par_level}
                    onChange={e => setForm({ ...form, par_level: e.target.value })}
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Storage Location</label>
                  <input
                    value={form.storage_location}
                    onChange={e => setForm({ ...form, storage_location: e.target.value })}
                    placeholder="Walk-in A, Dry Storage..."
                  />
                </div>
              </div>
              <div className="flex items-center gap-4 mb-4">
                <label className="flex items-center gap-2 text-sm" style={{ cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="is_food"
                    checked={form.is_food === true}
                    onChange={() => setForm({ ...form, is_food: true })}
                  />
                  Food item
                </label>
                <label className="flex items-center gap-2 text-sm" style={{ cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="is_food"
                    checked={form.is_food === false}
                    onChange={() => setForm({ ...form, is_food: false })}
                  />
                  Non-food / supplies / bar
                </label>
              </div>
              <div className="flex gap-2">
                <button className="btn btn-primary" type="submit" disabled={saving}>
                  {saving ? 'Adding...' : 'Add Item'}
                </button>
                <button className="btn btn-ghost" type="button" onClick={() => setShowForm(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Inventory Table */}
      {loading ? (
        <div className="loading">Loading inventory...</div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <div className="emoji">📦</div>
          <p>{belowPar ? 'All items are above par!' : 'No inventory items. Add one above.'}</p>
        </div>
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Category</th>
                  <th>On Hand</th>
                  <th>Par Level</th>
                  <th>Stock %</th>
                  <th>Location</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => {
                  const pct = parPct(item);
                  const isEditing = editingQty === item.id;
                  const showHistory = historyItemId === item.id;
                  return (
                    <React.Fragment key={item.id}>
                      <tr>
                        <td><strong>{item.name}</strong></td>
                        <td><span className="badge badge-gray">{item.category || '—'}</span></td>
                        <td>
                          {isEditing ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="0"
                                step="0.5"
                                value={editQtyVal}
                                onChange={e => setEditQtyVal(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') saveQty(item);
                                  if (e.key === 'Escape') setEditingQty(null);
                                }}
                                autoFocus
                                style={{ width: 72, padding: '4px 8px' }}
                              />
                              <button className="btn btn-primary btn-sm" onClick={() => saveQty(item)}>✓</button>
                              <button className="btn btn-ghost btn-sm" onClick={() => setEditingQty(null)}>✕</button>
                            </div>
                          ) : (
                            <span
                              onClick={() => { setEditingQty(item.id); setEditQtyVal(item.quantity_on_hand); }}
                              style={{ cursor: 'pointer', borderBottom: '1px dashed #9ca3af' }}
                              title="Click to edit"
                            >
                              {item.quantity_on_hand} <span className="text-gray text-xs">{item.unit}</span>
                            </span>
                          )}
                        </td>
                        <td>{item.par_level} <span className="text-gray text-xs">{item.unit}</span></td>
                        <td>
                          <div style={{ minWidth: 100 }}>
                            <span className={`badge ${parBadge(pct)}`}>{pct}%</span>
                            <div className="progress-bar mt-2">
                              <div className={`progress-fill ${parClass(pct)}`} style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        </td>
                        <td className="text-gray text-sm">{item.storage_location || '—'}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <button
                            className="btn btn-ghost btn-sm"
                            title="Edit item"
                            style={{ fontSize: 13, padding: '2px 8px', color: editingItem === item.id ? '#0d9488' : '#64748b' }}
                            onClick={() => editingItem === item.id ? setEditingItem(null) : startEdit(item)}
                          >
                            ✏️
                          </button>
                          <button
                            className="btn btn-ghost btn-sm"
                            title="View history"
                            style={{ fontSize: 14, padding: '2px 8px', color: showHistory ? '#0d9488' : '#94a3b8' }}
                            onClick={() => toggleHistory(item.id)}
                          >
                            🕐
                          </button>
                        </td>
                      </tr>
                      {editingItem === item.id && (
                        <tr>
                          <td colSpan={8} style={{ padding: 0, background: '#f0f9ff', borderBottom: '2px solid #bae6fd' }}>
                            <div style={{ padding: '14px 16px' }}>
                              <div style={{ fontWeight: 700, fontSize: 13, color: '#0369a1', marginBottom: 12 }}>Edit — {item.name}</div>
                                {/* Row 1: Name, Category, Unit */}
                              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
                                <div>
                                  <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, marginBottom: 3, textTransform: 'uppercase' }}>Name</div>
                                  <input style={{ width: '100%', padding: '5px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
                                    value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
                                </div>
                                <div>
                                  <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, marginBottom: 3, textTransform: 'uppercase' }}>Category</div>
                                  <input
                                    list={`cats-${item.id}`}
                                    style={{ width: '100%', padding: '5px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
                                    value={editForm.category}
                                    onChange={e => setEditForm(f => ({ ...f, category: e.target.value }))}
                                    placeholder="Pick or type…"
                                  />
                                  <datalist id={`cats-${item.id}`}>
                                    {categories.map(c => <option key={c} value={c} />)}
                                  </datalist>
                                </div>
                                <div>
                                  <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, marginBottom: 3, textTransform: 'uppercase' }}>Unit</div>
                                  <input style={{ width: '100%', padding: '5px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
                                    value={editForm.unit} onChange={e => setEditForm(f => ({ ...f, unit: e.target.value }))} placeholder="lb, case…" />
                                </div>
                              </div>
                              {/* Row 2: On Hand, Par Level, Location, Food toggle */}
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr 1fr', gap: 10, marginBottom: 12 }}>
                                <div>
                                  <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, marginBottom: 3, textTransform: 'uppercase' }}>On Hand</div>
                                  <input type="number" min="0" step="0.5" style={{ width: '100%', padding: '5px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
                                    value={editForm.quantity_on_hand} onChange={e => setEditForm(f => ({ ...f, quantity_on_hand: e.target.value }))} />
                                </div>
                                <div>
                                  <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, marginBottom: 3, textTransform: 'uppercase' }}>Par Level</div>
                                  <input type="number" min="0" step="0.5" style={{ width: '100%', padding: '5px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
                                    value={editForm.par_level} onChange={e => setEditForm(f => ({ ...f, par_level: e.target.value }))} />
                                </div>
                                <div>
                                  <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, marginBottom: 3, textTransform: 'uppercase' }}>Location</div>
                                  <input
                                    list={`locs-${item.id}`}
                                    style={{ width: '100%', padding: '5px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13 }}
                                    value={editForm.storage_location}
                                    onChange={e => setEditForm(f => ({ ...f, storage_location: e.target.value }))}
                                    placeholder="Pick or type…"
                                  />
                                  <datalist id={`locs-${item.id}`}>
                                    {locations.map(l => <option key={l} value={l} />)}
                                  </datalist>
                                </div>
                                <div>
                                  <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, marginBottom: 3, textTransform: 'uppercase' }}>Type</div>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingTop: 2 }}>
                                    <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                                      <input type="radio" checked={editForm.is_food === true} onChange={() => setEditForm(f => ({ ...f, is_food: true }))} /> Food
                                    </label>
                                    <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                                      <input type="radio" checked={editForm.is_food === false} onChange={() => setEditForm(f => ({ ...f, is_food: false }))} /> Non-food
                                    </label>
                                  </div>
                                </div>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                                <button className="btn btn-ghost btn-sm" onClick={() => setEditingItem(null)}>Cancel</button>
                                <button className="btn btn-primary btn-sm" onClick={() => saveEdit(item.id)} disabled={editSaving}>
                                  {editSaving ? 'Saving…' : 'Save Changes'}
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      {showHistory && (
                        <tr>
                          <td colSpan={8} style={{ padding: 0, background: '#f8fafc' }}>
                            <div style={{ padding: '12px 16px' }}>
                              <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a', marginBottom: 10 }}>
                                History — {item.name}
                              </div>
                              {historyLoading ? (
                                <div style={{ color: '#94a3b8', fontSize: 13 }}>Loading…</div>
                              ) : historyLog.length === 0 ? (
                                <div style={{ color: '#94a3b8', fontSize: 13 }}>No history yet — changes will appear here after the first update.</div>
                              ) : (
                                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                                      <th style={{ textAlign: 'left', padding: '4px 10px 6px 0', color: '#64748b', fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>Date</th>
                                      <th style={{ textAlign: 'left', padding: '4px 10px 6px 0', color: '#64748b', fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>Type</th>
                                      <th style={{ textAlign: 'right', padding: '4px 10px 6px 0', color: '#64748b', fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>Before</th>
                                      <th style={{ textAlign: 'right', padding: '4px 10px 6px 0', color: '#64748b', fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>Change</th>
                                      <th style={{ textAlign: 'right', padding: '4px 10px 6px 0', color: '#64748b', fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>Total</th>
                                      <th style={{ textAlign: 'left', padding: '4px 0 6px 10px', color: '#64748b', fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>By</th>
                                      <th style={{ textAlign: 'left', padding: '4px 0 6px 10px', color: '#64748b', fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>Note</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {historyLog.map(row => {
                                      const typeLabels = {
                                        invoice_receive: { label: 'Invoice', color: '#0d9488', bg: '#f0fdfa' },
                                        manual_edit:     { label: 'Manual', color: '#7c3aed', bg: '#faf5ff' },
                                        inventory_scan:  { label: 'Scan', color: '#2563eb', bg: '#eff6ff' },
                                        created:         { label: 'Created', color: '#166534', bg: '#f0fdf4' },
                                      };
                                      const t = typeLabels[row.change_type] || { label: row.change_type, color: '#64748b', bg: '#f8fafc' };
                                      const changePositive = row.quantity_change > 0;
                                      return (
                                        <tr key={row.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                          <td style={{ padding: '6px 10px 6px 0', color: '#475569', whiteSpace: 'nowrap' }}>
                                            {new Date(row.created_at).toLocaleDateString()} {new Date(row.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                          </td>
                                          <td style={{ padding: '6px 10px 6px 0' }}>
                                            <span style={{ background: t.bg, color: t.color, padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{t.label}</span>
                                          </td>
                                          <td style={{ padding: '6px 10px 6px 0', textAlign: 'right', color: '#64748b' }}>
                                            {row.quantity_before != null ? `${row.quantity_before} ${item.unit}` : '—'}
                                          </td>
                                          <td style={{ padding: '6px 10px 6px 0', textAlign: 'right', fontWeight: 700, color: row.quantity_change == null ? '#94a3b8' : changePositive ? '#16a34a' : '#dc2626' }}>
                                            {row.quantity_change == null ? '—' : `${changePositive ? '+' : ''}${row.quantity_change} ${item.unit}`}
                                          </td>
                                          <td style={{ padding: '6px 10px 6px 0', textAlign: 'right', fontWeight: 700, color: '#0f172a' }}>
                                            {row.quantity_after} {item.unit}
                                          </td>
                                          <td style={{ padding: '6px 0 6px 10px', color: '#475569' }}>
                                            {row.changed_by_name || '—'}
                                          </td>
                                          <td style={{ padding: '6px 0 6px 10px', color: '#94a3b8', fontSize: 12 }}>
                                            {row.note || '—'}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="text-xs text-gray" style={{ padding: '8px 16px', borderTop: '1px solid #f3f4f6' }}>
            Tip: click any quantity to edit it inline
          </div>
        </div>
      )}
    </div>
  );
}
